/**
 * RedirectBoundary
 *
 * Mounted at the top of the router. On every pathname change it asks the
 * backend `/api/redirects/check` whether the current URL is a legacy path
 * that must be redirected. If so, it replaces the client-side location to
 * the new target. Build-time prerender emits a `<meta http-equiv="refresh">`
 * for the same URL so Googlebot gets the redirect even before JS executes.
 *
 * This is deliberately *in addition to* a `_redirects` file (static hosts)
 * or an ingress-level 301 — those remain the preferred real-301 vehicles.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function RedirectBoundary() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const path = location.pathname;
    // Skip static/public paths and known-good app paths quickly
    if (!path || path.startsWith('/api/') || path.startsWith('/admin')) return;

    axios
      .get(`${BACKEND}/api/redirects/check`, { params: { path } })
      .then((res) => {
        if (cancelled || !res.data || !res.data.match) return;
        const { to, status } = res.data;
        if (status === 410 || !to) {
          // Gone — route the user to a 410 explainer page (reuse NotFoundPage
          // visual for now; copy explains the content was retired).
          navigate('/not-found-gone', { replace: true, state: { gone: path } });
          return;
        }
        if (to && to !== path) {
          // External URL (http...) → full page nav; internal → router replace
          if (to.startsWith('http')) {
            window.location.replace(to);
          } else {
            navigate(to, { replace: true });
          }
        }
      })
      .catch(() => {
        /* non-fatal: a down redirect API must not break navigation */
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  return null;
}
